import React from 'react';
import { getMemberDisplay } from './memberDisplayCore';

/**
 * Единый слой отображения имени/ника/алиаса члена организации (визуальный «Вариант A»).
 *
 * Чистая логика нормализации (getMemberDisplay) вынесена в ./memberDisplayCore (без JSX,
 * чтобы её можно было переиспользовать/тестировать вне React — см. resultsExport.js).
 * Здесь остались презентационные компоненты.
 *
 * Визуальное правило (Вариант A — единая иерархия):
 *   - имя ВСЕГДА одинаково тёмное/жирное (и у реального, и у гостя) — никаких серых плашек;
 *   - тип передаётся аватаром: реальный — залитый индиго-кружок с инициалами,
 *     гость — кружок с пунктирной обводкой (или иконкой человека, если имени нет);
 *   - вторая строка: реальный → синий @username; гость → «Гость» / «ожидает входа».
 *
 * «ожидает входа» — гость, которому администратор ввёл @username, но этот человек ещё
 * не заходил в бота (telegramUserId неизвестен → memberType остаётся GUEST, есть pendingUsername).
 *
 * getMemberDisplay принимает объект любой формы (registration / результат поиска /
 * игрок турнира / роль) и нормализует к
 * { isGuest, name, aliasTag, nick, awaiting, initials }.
 */

// Ре-экспорт для существующих импортов `import { getMemberDisplay } from '.../memberDisplay'`.
export { getMemberDisplay };

// ─── Презентационные примитивы ──────────────────────────────────────────────────

const PersonIcon = ({ size = 16, color = '#94a3b8' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

/**
 * Аватар члена (Вариант A).
 *   реальный → залитый индиго-кружок с белыми инициалами;
 *   гость    → кружок с пунктирной обводкой и приглушёнными инициалами
 *              (иконка человека, если имени/инициалов нет).
 */
export const MemberAvatar = ({ view, size = 36, style }) => {
  const base = {
    width: size, height: size, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, fontSize: Math.round(size * 0.38), fontWeight: 700,
    boxSizing: 'border-box', ...style,
  };
  if (view.isGuest) {
    return (
      <div style={{ ...base, background: '#fff', border: '1.5px dashed #cbd5e1', color: '#94a3b8' }}>
        {view.initials || <PersonIcon size={Math.round(size * 0.5)} color="#94a3b8" />}
      </div>
    );
  }
  return (
    <div style={{ ...base, color: '#fff', background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
      {view.initials || <PersonIcon size={Math.round(size * 0.5)} color="#fff" />}
    </div>
  );
};

/** Серый чип с alias-пометкой реального пользователя. */
export const AliasTag = ({ text, style }) => {
  if (!text) return null;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, color: '#6b7280', background: '#f3f4f6',
      borderRadius: 6, padding: '1px 6px', whiteSpace: 'nowrap', flexShrink: 0, ...style,
    }}>
      {text}
    </span>
  );
};

/**
 * Имя члена (Вариант A) — единый тёмный текст и для гостя, и для реального.
 * Цвет/размер переопределяются через style.
 */
export const MemberNameText = ({ view, style = {} }) => (
  <span style={{
    fontWeight: 600, color: '#111827', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 14, ...style,
  }}>
    {view.name}
  </span>
);

/**
 * @ник члена — приглушённый индиго (единый стиль для гостя и реального).
 * Кликабельный только для реального (у гостя аккаунта в системе ещё нет).
 */
export const NickBadge = ({ view, onClick, style }) => {
  if (!view.nick) return null;
  const clickable = !!onClick && !view.isGuest;
  return (
    <span
      onClick={clickable ? onClick : undefined}
      style={{
        fontSize: 12, color: '#6366f1', opacity: 0.7,
        cursor: clickable ? 'pointer' : 'default', whiteSpace: 'nowrap', ...style,
      }}
    >
      @{view.nick}
    </span>
  );
};

/**
 * Полный блок участника (Вариант A): аватар + имя (+ alias-чип) + вторая строка.
 * Вторая строка (showSubtitle):
 *   реальный → синий @username (кликабельный, если задан onNickClick);
 *   гость    → «Гость» либо «@ник · ожидает входа» / «Гость · ожидает входа».
 * Используется там, где нужна информация о типе: список участников события,
 * поиск, создание турнира, редактирование состава (черновик/во время старта).
 */
export const MemberIdentity = ({
  member, view: viewProp, size = 36, showSubtitle = true, onNickClick, style,
}) => {
  const view = viewProp || getMemberDisplay(member);

  let subtitle = null;
  if (showSubtitle) {
    if (view.isGuest) {
      if (view.awaiting) {
        const prefix = view.nick ? `@${view.nick} · ` : 'Гость · ';
        subtitle = <span style={{ color: '#94a3b8' }}>{prefix}ожидает входа</span>;
      } else {
        subtitle = <span style={{ color: '#9ca3af' }}>Гость</span>;
      }
    } else if (view.nick) {
      subtitle = (
        <span
          onClick={onNickClick}
          style={{ color: '#6366f1', cursor: onNickClick ? 'pointer' : 'default' }}
        >
          @{view.nick}
        </span>
      );
    }
  }

  // В списке участников события имя/алиас/ник должны быть видны целиком —
  // не обрезаем многоточием, а разрешаем перенос на новую строку (см. wrap-стили ниже).
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, ...style }}>
      <MemberAvatar view={view} size={size} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, minWidth: 0 }}>
          <MemberNameText
            view={view}
            style={{
              fontSize: 15,
              whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip',
              overflowWrap: 'anywhere', wordBreak: 'break-word',
            }}
          />
          <AliasTag text={view.aliasTag} />
        </div>
        {subtitle && (
          <div style={{
            fontSize: 12.5, marginTop: 1,
            whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word',
          }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Компактная однострочная подпись: имя + alias-чип (+ опц. @ник).
 * Имя единое тёмное (без серых плашек), БЕЗ пометки «Гость»/«ожидает входа».
 * По умолчанию @ник НЕ показывается — в плотных турнирных вью (лидерборд, матчи,
 * отдыхающие, ввод счёта) он только тянет строку и мешает уместить команды/счёт.
 * Имя гостя без alias всё равно показывает свой `@ник` (это его единственный
 * идентификатор — он зашит в `name`). При необходимости @ник включается showNick.
 */
export const MemberInline = ({
  member, view: viewProp, fontSize = 13, color = '#111827',
  showNick = false, onNickClick, style,
}) => {
  const view = viewProp || getMemberDisplay(member);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, maxWidth: '100%', ...style,
    }}>
      <MemberNameText
        view={view}
        style={{ fontSize, color, fontWeight: 500, minWidth: 0, flex: '0 1 auto' }}
      />
      <AliasTag text={view.aliasTag} style={{ fontSize: Math.max(9, fontSize - 3) }} />
      {showNick && (
        <NickBadge view={view} onClick={onNickClick} style={{ fontSize: Math.max(9, fontSize - 3), flexShrink: 0 }} />
      )}
    </span>
  );
};

/**
 * Имя + `@ник` в ДВЕ строки (ник мельче и приглушён под именем).
 * Для узких плотных вью, где `@ник` в одну строку не помещается рядом с именем
 * (карточка матча с двумя командами и счётом, окно ввода счёта).
 * `align` управляет выравниванием (нужно для правой команды в карточке матча).
 */
export const MemberStack = ({ member, view: viewProp, fontSize = 13, color = '#374151', align = 'left' }) => {
  const view = viewProp || getMemberDisplay(member);
  const justify = align === 'right' ? 'flex-end' : 'flex-start';
  return (
    <div style={{ minWidth: 0, maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: justify, minWidth: 0 }}>
        <MemberNameText view={view} style={{ fontSize, color, fontWeight: 500, minWidth: 0 }} />
        <AliasTag text={view.aliasTag} style={{ fontSize: Math.max(9, fontSize - 3) }} />
      </div>
      {view.nick && (
        <div style={{
          fontSize: Math.max(10, fontSize - 2), color: '#9ca3af', textAlign: align,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25,
        }}>
          @{view.nick}
        </div>
      )}
    </div>
  );
};

/**
 * Игрок в рамке-чипе: имя + alias-чип + `@ник` внутри обводки.
 * Для списков, где игроки идут подряд и сливаются (например «Отдыхают» в раунде) —
 * рамка явно показывает, где заканчивается один и начинается следующий.
 */
export const MemberChip = ({ member, view: viewProp, fontSize = 12 }) => {
  const view = viewProp || getMemberDisplay(member);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%',
      padding: '2px 8px', borderRadius: 8,
      border: '1px solid #e5e7eb', background: '#fff',
    }}>
      <MemberNameText view={view} style={{ fontSize, color: '#374151', fontWeight: 500 }} />
      <AliasTag text={view.aliasTag} style={{ fontSize: Math.max(9, fontSize - 3) }} />
      {view.nick && (
        <span style={{ fontSize: Math.max(10, fontSize - 1), color: '#9ca3af', whiteSpace: 'nowrap' }}>
          @{view.nick}
        </span>
      )}
    </span>
  );
};
